<script setup lang="ts">
import { BAlert, BForm, BFormGroup, BFormInput, BLink, BModal } from 'bootstrap-vue-next';
import { ref } from 'vue';

import { setCrowdinUsername } from '~/api/users';
import LoadingButton from '~/common/components/LoadingButton.vue';

const props = withDefaults(defineProps<{
  userId: number;
  currentUsername?: string | null;
}>(), {
  currentUsername: '',
});

const model = defineModel<boolean>();
const emit = defineEmits<{ crowdinUsernameSet: [crowdinUsername: string] }>();

const username = ref('');
const error = ref<string | null>(null);
const loading = ref(false);

const resetForm = () => {
  username.value = '';
  error.value = null;
};

const save = async () => {
  error.value = null;
  loading.value = true;

  try {
    const crowdinUsername = username.value.trim();
    await setCrowdinUsername(props.userId, crowdinUsername);
    emit('crowdinUsernameSet', crowdinUsername);
    model.value = false;
  } catch {
    error.value = 'Failed to save. Please try again.';
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <BModal
    v-model="model"
    title="Link Crowdin account"
    no-footer
    @hidden="resetForm"
  >
    <BAlert
      :model-value="!!error"
      variant="danger"
    >
      {{ error }}
    </BAlert>

    <p class="mb-3">
      GitLab is translated by the community on Crowdin. Link your
      <!-- eslint-disable vue/multiline-html-element-content-newline -->
      <BLink
        is-unsafe-link
        href="https://crowdin.com/project/gitlab-ee"
        target="_blank"
      >Crowdin</BLink>
      <!-- eslint-enable vue/multiline-html-element-content-newline -->
      username to receive contribution points for your translation contributions.
    </p>

    <p
      v-if="currentUsername"
      class="mb-3"
      data-testid="currently-linked"
    >
      Currently linked: <strong>{{ currentUsername }}</strong>
    </p>

    <BForm @submit.prevent="save">
      <BFormGroup
        label="Crowdin username"
        class="mb-3"
      >
        <BFormInput
          v-model="username"
          :placeholder="currentUsername || 'Enter Crowdin username'"
          maxlength="60"
          required
        />
      </BFormGroup>

      <LoadingButton
        type="submit"
        class="float-end"
        variant="primary"
        :loading="loading"
      >
        Save
      </LoadingButton>
    </BForm>
  </BModal>
</template>
